/**
 * A fight card from v1 `bouts[]` (GET /v1/events/{id}), top of card first. Each bout offers
 * one button per `watch[]` entry and none when it is empty (bkfc-cms domain.md Z.13.2).
 */
import React from 'react';
import { Link } from 'react-router-dom';

const watchLabel = entry => (entry.role === 'replay' ? 'Watch fight' : 'Watch in full card');

/** "UD · R5", "Result pending" (never a draw), or null before the fight. */
export function resultLabel(bout) {
  if (bout.result_state === 'undetermined') {
    return 'Result pending';
  }
  if (bout.result_state === 'cancelled' || bout.result_state === 'postponed') {
    return bout.result_state === 'cancelled' ? 'Cancelled' : 'Postponed';
  }
  const r = bout.current_result;
  if (bout.result_state !== 'determined' || !r) {
    return null;
  }
  const method = r.method?.name || r.method_raw;
  return [method, r.end_round ? `R${r.end_round}` : null].filter(Boolean).join(' · ') || null;
}

const Corner = ({ participant }) => {
  if (!participant) {
    return <span className="text-white/50">TBD</span>;
  }
  const won = participant.outcome === 'win';
  const name = participant.display_name || participant.fighter?.name || 'TBD';
  return (
    <span className={won ? 'text-accent font-semibold' : 'text-white'}>
      {participant.fighter?.slug ? (
        <Link to={`/fighters/${participant.fighter.slug}`} className="hover:underline">
          {name}
        </Link>
      ) : (
        name
      )}
    </span>
  );
};

const statsLine = (totals, side) =>
  `${totals[side].strike_landed}/${totals[side].strike_attempt} strikes, ${totals[side].knockdown} KD`;

const BoutRow = ({ bout, stats }) => {
  const red = bout.participants?.find(p => p.corner === 'red');
  const blue = bout.participants?.find(p => p.corner === 'blue');
  const result = resultLabel(bout);
  const watch = bout.watch || [];
  const totals = stats?.totals;

  return (
    <li className="py-4 border-b border-white/10" data-testid="bout">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-lg uppercase">
        <Corner participant={red} />
        <span className="text-white/50 text-sm">vs</span>
        <Corner participant={blue} />
      </div>
      <div className="flex flex-wrap gap-x-3 text-sm text-white/70">
        {bout.weight_class?.name && <span>{bout.weight_class.name}</span>}
        {bout.title_fight && <span>Title fight</span>}
        {result && <span data-testid="bout-result">{result}</span>}
      </div>
      {totals && (
        <p className="text-xs text-white/60 mt-1" data-testid="bout-stats">
          {red?.display_name || 'Red'}: {statsLine(totals, 'red')} · {blue?.display_name || 'Blue'}:{' '}
          {statsLine(totals, 'blue')}
        </p>
      )}
      {watch.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="bout-watch">
          {watch.map(entry => (
            <Link
              key={`${entry.media_id}-${entry.role}`}
              to={`/watch/${entry.slug || entry.media_id}`}
              className="px-3 py-1 text-sm font-semibold rounded bg-accent text-primary"
            >
              {watchLabel(entry)}
            </Link>
          ))}
        </div>
      )}
    </li>
  );
};

/**
 * @param {{bouts: object[], stats?: object[]}} props `stats` is `/v1/events/{id}/stats` `bouts[]`
 */
const BoutList = ({ bouts, stats = [] }) => {
  const statsById = new Map(stats.map(s => [s.bout_id, s]));
  const ordered = [...bouts].sort((a, b) => a.order_index - b.order_index);
  return (
    <ul>
      {ordered.map(bout => (
        <BoutRow key={bout.id} bout={bout} stats={statsById.get(bout.id)} />
      ))}
    </ul>
  );
};

export default BoutList;
